import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useRef,
  useCallback
} from 'react';
import { useToggle } from '../../hooks/useToggle';
import {
  Coordinates,
  GoogleMapsContextI,
  MapModalData,
  MapOptions
} from './types';

const GoogleMapsContext = createContext<GoogleMapsContextI | null>(null);

export const GoogleMapsProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [isInitializedMap, setIsInitializedMap] = useState(false);
  const [isOpenModal, toggleModal] = useToggle(false);

  const mapRef = useRef<Coordinates | null>(null);

  const setMapRef = useCallback((map: Coordinates | null) => {
    mapRef.current = map;
  }, []);

  const [mapData, setMapData] = useState<MapOptions>({
    initialLocation: undefined,
    isMarkerDraggable: true
  });

  const [mapModalData, setMapModalData] = useState<MapModalData>({
    modalId: 'map-modal',
    onClose: null,
    saveCoordinates: null
  });

  const value = useMemo(
    () => ({
      isOpenModal,
      toggleModal,
      mapData,
      setMapData,
      isInitializedMap,
      setIsInitializedMap,
      mapModalData,
      setMapModalData,
      mapRef,
      setMapRef
    }),
    [
      mapData,
      setMapData,
      isInitializedMap,
      setIsInitializedMap,
      isOpenModal,
      toggleModal,
      mapModalData,
      setMapModalData,
      setMapRef
    ]
  );

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext);
  if (context === null) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
  }
  return context;
};
